// docs/69 ST5 — a unit's statuses in the HUD: the board's badges (the same 7×7 glyphs, at 2×) in
// a row, each with turn pips, and a tooltip with the rule it plays by. Used on the party's cards
// and the foes' rows; the hover chip on the board says the same in a line (`statusesLine`).
import { useState } from 'react';
import { Tooltip } from '../components';
import { STATUS_INFO, cssColour, glyphInk, shownStatuses, statusLabel, statusNote, turnsLeft, type UnitNamer } from '../../core/sim/statuses';
import { useEncounter } from '../../core/encounter/session';
import { statusGloss } from '../../core/sim/describe';
import type { StatusKind, StatusTag } from '../../core/sim/types';

/** The badge as the board draws it (textures.ts `badge`), as crisp SVG. */
export function StatusIcon({ kind, px = 18 }: { kind: StatusKind; px?: number }) {
  const info = STATUS_INFO[kind];
  if (!info) return null;
  const ink = glyphInk(info.colour);
  return (
    <svg width={px} height={px} viewBox="0 0 9 9" shapeRendering="crispEdges" aria-hidden="true" className="fk-status__icon">
      <rect x="1" y="0" width="7" height="9" fill="#0b1020" /><rect x="0" y="1" width="9" height="7" fill="#0b1020" />
      <rect x="1" y="1" width="7" height="7" fill={cssColour(info.colour)} />
      {info.icon.flatMap((row, j) => [...row].map((ch, i) => (ch === '#' ? <rect key={`${i},${j}`} x={i + 1} y={j + 1} width="1" height="1" fill={ink} /> : null)))}
    </svg>
  );
}

/** docs/104 PD2: names a unit on the board the scene draws (a Shielded friend's paladin). */
function useUnitNamer(): UnitNamer {
  const units = useEncounter((s) => s.boardSnapshot?.units);
  return (id) => units?.find((u) => u.unitId === id)?.name;
}

function Chip({ tag, px, nameOf }: { tag: StatusTag; px: number; nameOf?: UnitNamer }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const info = STATUS_INFO[tag.kind];
  const pips = Math.min(3, tag.pulses ?? 0);
  const left = turnsLeft(tag.pulses);
  return (
    <span className={`fk-status fk-status--${info.tone}`} data-testid="status-chip" data-status={tag.kind} aria-label={statusNote(tag, nameOf)} tabIndex={-1}
      onMouseEnter={(e) => setAnchor(e.currentTarget)} onMouseLeave={() => setAnchor(null)}>
      <StatusIcon kind={tag.kind} px={px} />
      {pips > 0 && <span className="fk-status__pips" aria-hidden="true">{Array.from({ length: pips }, (_, i) => <i key={i} style={{ background: cssColour(info.colour) }} />)}</span>}
      <Tooltip anchor={anchor} title={<span className="fk-status__tip-title"><StatusIcon kind={tag.kind} px={14} /> {statusLabel({ ...tag, pulses: undefined }, nameOf)}</span>}
        terms={left ? `Wears off: ${left === 'this turn' ? 'after this turn' : `in ${left}`}` : tag.kind === 'Injured' ? 'Until it rests' : 'Until something clears it'}>
        {cap(statusGloss(tag.kind, tag.magnitude))}.
      </Tooltip>
    </span>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The row of a unit's statuses (nothing when it carries none). Downed is left to the card's own look. */
export function StatusChips({ statuses, skip = ['Downed'], px = 18 }: { statuses: readonly StatusTag[] | undefined; skip?: readonly StatusKind[]; px?: number }) {
  const nameOf = useUnitNamer();
  const shown = shownStatuses(statuses).filter((t) => !skip.includes(t.kind));
  if (!shown.length) return null;
  return <span className="fk-statuses" data-testid="status-chips">{shown.map((t) => <Chip key={t.kind} tag={t} px={px} nameOf={nameOf} />)}</span>;
}
